import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { DirectorySearchResult } from "@/services/directory-search-service"
import type { ParticipantRole, Project } from "@/services/project-service"

import { DirectorySearch } from "@/components/directory-search"
import { LogOut, Share2, UserPlus } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import {
  useAddParticipant,
  useProjectParticipants,
  useRemoveParticipant,
  useTransferOwnership,
  useUpdateParticipantRole,
} from "@/hooks/use-projects"
import { SettingsService } from "@/services/settings-service"

import { ParticipantList } from "./participant-list"

type ProjectShareDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  project: Project | undefined
  onLeave?: () => void
}

// Only reader, editor, and admin can be added - owner is assigned via transfer
type AddableRole = "reader" | "editor" | "admin"

export function ProjectShareDialog({
  open,
  onOpenChange,
  projectId,
  project,
  onLeave,
}: ProjectShareDialogProps) {
  const { t } = useTranslation()
  const [newParticipantRole, setNewParticipantRole] = useState<AddableRole>("reader")
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)

  // Get current user OID from auth claims
  const currentUserOid = SettingsService.getInstance().claims.oid

  // Fetch participants
  const { data: participantsData, isLoading } = useProjectParticipants(projectId)

  // Mutations
  const addParticipant = useAddParticipant()
  const updateParticipantRole = useUpdateParticipantRole()
  const removeParticipant = useRemoveParticipant()
  const transferOwnership = useTransferOwnership()

  // Check if current user can manage participants
  const canManage = project?.currentUserRole === "owner" || project?.currentUserRole === "admin"

  // Any non-owner direct user participant can leave (aligns with backend self-removal rule)
  const isDirectParticipant =
    (participantsData?.participants ?? []).some(
      (p) => p.oid === currentUserOid && p.type === "user"
    ) && project?.currentUserRole !== "owner"
  const canLeave = isDirectParticipant

  // Get existing participant OIDs to filter search results
  const existingOids = (participantsData?.participants.map((p) => p.oid) ?? []).filter(
    Boolean
  ) as string[]

  const handleAddParticipant = async (result: DirectorySearchResult) => {
    if (existingOids.includes(result.oid)) {
      return // Already a participant
    }

    await addParticipant.mutateAsync({
      projectId,
      participant: {
        oid: result.oid,
        displayName: result.displayName,
        type: result.type,
        role: newParticipantRole,
      },
    })
  }

  const handleRoleChange = async (participantOid: string, role: ParticipantRole) => {
    await updateParticipantRole.mutateAsync({
      projectId,
      participantOid,
      role,
    })
  }

  const handleRemove = async (participantOid: string) => {
    await removeParticipant.mutateAsync({
      projectId,
      participantOid,
    })
  }

  const handleTransferOwnership = async (newOwnerOid: string) => {
    await transferOwnership.mutateAsync({
      projectId,
      newOwnerOid,
    })
  }

  const handleConfirmLeave = async () => {
    if (!currentUserOid) return
    try {
      await removeParticipant.mutateAsync({
        projectId,
        participantOid: currentUserOid,
      })
      setShowLeaveDialog(false)
      onOpenChange(false)
      onLeave?.()
    } catch {
      // Error is handled by the mutation's global error handler
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="size-5" />
              {t("sharing.dialog_title", { defaultValue: "Share Project" })}
            </DialogTitle>
            <DialogDescription>
              {t("sharing.dialog_description", {
                defaultValue: "Add users or groups to collaborate on this project.",
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Add participant section */}
            {canManage && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <UserPlus className="text-muted-foreground size-4" />
                  <Label className="text-sm font-medium">
                    {t("sharing.add_participant", { defaultValue: "Add People" })}
                  </Label>
                </div>

                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <DirectorySearch
                      disabled={addParticipant.isPending}
                      selectedOids={existingOids}
                      onSelect={handleAddParticipant}
                    />
                  </div>
                  <Select
                    value={newParticipantRole}
                    onValueChange={(v) => setNewParticipantRole(v as AddableRole)}
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reader">
                        {t("sharing.role_reader", { defaultValue: "Reader" })}
                      </SelectItem>
                      <SelectItem value="editor">
                        {t("sharing.role_editor", { defaultValue: "Editor" })}
                      </SelectItem>
                      <SelectItem value="admin">
                        {t("sharing.role_admin", { defaultValue: "Admin" })}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator />
              </div>
            )}

            {/* Participants list */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                {t("sharing.participants", { defaultValue: "People with Access" })}
              </Label>
              <ParticipantList
                canManage={canManage}
                currentUserOid={currentUserOid}
                isCurrentUserOwner={project?.currentUserRole === "owner"}
                isLoading={isLoading}
                ownerUserId={participantsData?.ownerUserId}
                participants={participantsData?.participants ?? []}
                onRemove={handleRemove}
                onRoleChange={handleRoleChange}
                onTransferOwnership={handleTransferOwnership}
              />
            </div>

            {/* Role descriptions */}
            <div className="bg-muted/50 text-muted-foreground rounded-md px-3 py-2 text-xs">
              <p className="mb-1 font-medium">
                {t("sharing.role_descriptions_title", { defaultValue: "Permission levels:" })}
              </p>
              <ul className="space-y-0.5">
                <li>
                  <strong>{t("sharing.role_reader", { defaultValue: "Reader" })}:</strong>{" "}
                  {t("sharing.role_reader_desc", {
                    defaultValue: "Can view project and create their own chats",
                  })}
                </li>
                <li>
                  <strong>{t("sharing.role_editor", { defaultValue: "Editor" })}:</strong>{" "}
                  {t("sharing.role_editor_desc", {
                    defaultValue: "Can also edit files and settings",
                  })}
                </li>
                <li>
                  <strong>{t("sharing.role_admin", { defaultValue: "Admin" })}:</strong>{" "}
                  {t("sharing.role_admin_desc", {
                    defaultValue: "Can also manage participants (except ownership)",
                  })}
                </li>
                <li>
                  <strong>{t("sharing.role_owner", { defaultValue: "Owner" })}:</strong>{" "}
                  {t("sharing.role_owner_desc", {
                    defaultValue: "Full control including managing access",
                  })}
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-center justify-between">
            {canLeave ? (
              <Button
                className="text-destructive hover:text-destructive gap-2"
                disabled={removeParticipant.isPending}
                variant="ghost"
                onClick={() => setShowLeaveDialog(true)}
              >
                <LogOut className="size-4" />
                {t("sharing.leave_project", { defaultValue: "Leave project" })}
              </Button>
            ) : (
              <span />
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.close", { defaultValue: "Close" })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("sharing.leave_project_title", { defaultValue: "Leave project?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("sharing.leave_project_description", {
                defaultValue:
                  "You will lose access to this project and its content. You will need to be invited again to regain access.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
            <AlertDialogAction
              disabled={removeParticipant.isPending}
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmLeave()
              }}
            >
              {t("sharing.leave_project", { defaultValue: "Leave project" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
